import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Trash2, Building2, Mail, User, Phone, MapPin, Briefcase, Globe, Linkedin } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../components/ConfirmModal";
import { useSettings, formatMoney } from "../lib/settings";
import MovesTimeline from "../components/MovesTimeline";
import NotesTimeline from "../components/NotesTimeline";
import HistoryTimeline from "../components/HistoryTimeline";
import CustomFieldsRenderer from "../components/CustomFieldsRenderer";
import ClientWidget from "../components/ClientWidget";
import DetailsCard from "../components/DetailsCard";

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const { currency } = useSettings();
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const [contact, setContact] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [deals, setDeals] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [users, setUsers] = useState([]);
  const [company, setCompany] = useState(null);

  const load = async () => {
    try {
      const [c, d, p, u] = await Promise.all([
        api.get(`/contacts/${id}`),
        api.get("/deals"),
        api.get("/pipelines"),
        api.get("/users"),
      ]);
      setContact(c.data);
      setDeals(d.data.filter(x => x.contact_id === id));
      setPipelines(p.data);
      setUsers(u.data);
      if (c.data.company_id) {
        try {
          const co = await api.get(`/companies/${c.data.company_id}`);
          setCompany(co.data);
        } catch { setCompany(null); }
      } else { setCompany(null); }
    } catch { toast.error("Not found"); navigate("/contacts"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = async (data) => {
    try {
      const { data: updated } = await api.patch(`/contacts/${id}`, data);
      setContact(updated);
      setHistoryKey(k => k + 1);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const remove = async () => {
    if (!await confirm({ title: "Delete this contact?", message: "This cannot be undone.", destructive: true, confirmLabel: "Delete" })) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Contact deleted");
    navigate("/contacts");
  };

  const stageName = (deal) => {
    const p = pipelines.find(p => p.id === deal.pipeline_id);
    return p?.stages.find(s => s.id === deal.stage_id)?.name || "—";
  };

  if (!contact) return <div className="px-8 py-12 text-mute mono text-xs">loading…</div>;

  return (
    <div data-testid="contact-detail-page">
      <div className="px-8 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/contacts")} className="pg-btn pg-btn-ghost"><ArrowLeft size={14} /> Back</button>
          <div>
            <h1 className="text-2xl" style={{ fontWeight: 600 }}>{contact.name}</h1>
            <div className="mono text-[11px] text-mute mt-1">{contact.company || "no company"} · {contact.role || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={remove} className="pg-btn pg-btn-danger" data-testid="delete-contact-btn" style={{ display: canDelete ? undefined : "none" }}><Trash2 size={14} /></button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 p-8">
        <div className="col-span-2 space-y-6">
          {company && (
            <div className="pg-card p-4">
              <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Linked Company</label>
              <Link to={`/companies/${company.id}`} className="inline-flex items-center gap-2 text-sm"
                    style={{ color: "var(--pg-accent-deep)", fontWeight: 600 }}
                    data-testid="contact-company-link">
                <Building2 size={14} /> {company.name}
                {company.domain && <span className="mono text-[11px] text-mute">· {company.domain}</span>}
              </Link>
            </div>
          )}

          <ClientWidget companyId={contact.company_id} />

          <CustomFieldsRenderer
            entity="contact"
            values={contact.custom_fields || {}}
            onSave={async (next) => { await patch({ custom_fields: next }); }}
          />

          <NotesTimeline
            readOnly
            linkedType="contact"
            linkedId={contact.id}
            source={`/contacts/${contact.id}/notes`}
          />

          <div className="pg-card p-6">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-4">Associated Deals</h3>
            {deals.length === 0 ? (
              <div className="text-mute mono text-xs">No deals linked</div>
            ) : (
              <div className="space-y-2">
                {deals.map((d) => (
                  <Link key={d.id} to={`/deals/${d.id}`} className="block pg-card p-3 hover:border-purple-500" style={{ background: "var(--pg-surface-2)" }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div>{d.name}</div>
                        <div className="mono text-[11px] text-mute mt-0.5">{stageName(d)} · {formatMoney(d.value, currency.symbol)} · {d.probability}%</div>
                      </div>
                      <span className="mono text-[11px] text-mute">{d.status}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <MovesTimeline
            linkedType="contact"
            linkedId={contact.id}
            users={users}
            contactEmail={contact.email}
            contactPhone={contact.phone}
            source={`/contacts/${contact.id}/moves`}
          />
          <HistoryTimeline key={historyKey} entityType="contact" entityId={contact.id} />
        </div>

        <div className="space-y-6">
          <DetailsCard
            testId="contact-details-card"
            createdAt={contact.created_at}
            updatedAt={contact.updated_at}
            onSave={patch}
            fields={[
              { key: "name",    label: "Name",     value: contact.name,     icon: User },
              { key: "email",   label: "Email",    value: contact.email,    icon: Mail,      type: "email" },
              { key: "phone",   label: "Phone",    value: contact.phone,    icon: Phone,     type: "tel" },
              { key: "company", label: "Company",  value: contact.company,  icon: Building2 },
              { key: "role",    label: "Role",     value: contact.role,     icon: Briefcase },
              { key: "address", label: "Address",  value: contact.address,  icon: MapPin,    type: "textarea" },
              { key: "website", label: "Website",  value: contact.website,  icon: Globe,     type: "url" },
              { key: "linkedin_url", label: "LinkedIn", value: contact.linkedin_url, icon: Linkedin, type: "url",
                externalHref: (v) => v.includes("linkedin.com") ? (v.startsWith("http") ? v : `https://${v}`) : `https://linkedin.com/in/${v.replace(/^\/+/, "")}` },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
